import { CellCoordinate, ScrollPosition, SortDescriptor } from '@ardoq/table';
import {
  APIAttachment,
  APIDocumentArchiveFolder,
  ArdoqId,
} from '@ardoq/api-types';
import { actionCreator } from '@ardoq/rxbeach';
import {
  TableFilterExpressionFrom,
  TableFilterList,
  TableFilterString,
} from '@ardoq/table-filter-bar';
import { Extend } from '@ardoq/common-helpers';
import { Trackable } from '../tracking/trackingData';

export const setFolderExpanded = actionCreator<string>(
  '[documentArchive] SET_FOLDER_EXPANDED'
);

export const setFolderClosed = actionCreator<string>(
  '[documentArchive] SET_FOLDER_CLOSED'
);

export const setEditedCell = actionCreator<CellCoordinate | null>(
  '[documentArchive] SET_EDITED_CELL'
);

export type RenameRowPayload = {
  typeId: string;
  version: number;
  id: string;
  newName: string;
};

export const renameFolder = actionCreator<Trackable<RenameRowPayload>>(
  '[documentArchive] RENAME_FOLDER'
);

export const renameWorkspaceFolder = actionCreator<Trackable<RenameRowPayload>>(
  '[documentArchive] RENAME_WORKSPACE_FOLDER'
);

export const renameAttachment = actionCreator<Trackable<RenameRowPayload>>(
  '[documentArchive] RENAME_ATTACHMENT'
);

export const renameWorkspaceAttachment = actionCreator<
  Trackable<RenameRowPayload>
>('[documentArchive] RENAME_WORKSPACE_ATTACHMENT');

export type MoveFolderPayload = {
  typeId: string;
  foldersWithNewParent: APIDocumentArchiveFolder[];
};

export const moveFolder = actionCreator<Trackable<MoveFolderPayload>>(
  '[documentArchive] MOVE_FOLDER'
);

export const moveWorkspaceFolder = actionCreator<Trackable<MoveFolderPayload>>(
  '[documentArchive] MOVE_WORKSPACE_FOLDER'
);

export type MoveAttachmentsPayload = {
  attachmentIds: ArdoqId[];
  folder: ArdoqId | null;
};

export const moveAttachments = actionCreator<Trackable<MoveAttachmentsPayload>>(
  '[documentArchive] MOVE_ATTACHMENTS'
);

export const moveWorkspaceAttachments = actionCreator<
  Trackable<MoveAttachmentsPayload>
>('[documentArchive] MOVE_WORKSPACE_ATTACHMENTS');

export const setSelectedIds = actionCreator<string[]>(
  '[documentArchive] SET_SELECTED_IDS'
);

export const requestDeletion = actionCreator<Trackable<DeleteRequest>>(
  '[documentArchive] REQUEST_DELETION'
);

export const cancelDelete = actionCreator<Trackable>(
  '[documentArchive] CANCEL_DELETE'
);

export type DeleteRequest = {
  folderIds: string[];
  attachmentIds: ArdoqId[];
};

export const confirmDelete = actionCreator<Trackable<DeleteRequest>>(
  '[documentArchive] CONFIRM_DELETE'
);

export const deleteFolders = actionCreator<string[]>(
  '[documentArchive] DELETE_FOLDERS'
);

export const deleteAttachments = actionCreator<string[]>(
  '[documentArchive] DELETE_ATTACHMENTS'
);

export type DownloadAttachmentPayload = {
  uri: string;
};

export const downloadAttachment = actionCreator<
  Trackable<DownloadAttachmentPayload>
>('[documentArchive] DOWNLOAD_ATTACHMENT');

type CreateFolderPayload = {
  name: string;
  typeId: string;
};

export const createFolder = actionCreator<Trackable<CreateFolderPayload>>(
  '[documentArchive] CREATE_FOLDER'
);

export const createWorkspaceFolder = actionCreator<
  Trackable<CreateFolderPayload>
>('[documentArchive] CREATE_WORKSPACE_FOLDER');

export type UploadFilesPayload = {
  existingAttachmentNames: string[];
  files: File[];
  typeId: string;
};

export const uploadFiles = actionCreator<Trackable<UploadFilesPayload>>(
  '[documentArchive] UPLOAD_FILES'
);

export const uploadFilesTooLarge = actionCreator<{
  fileNames: string;
  maxFileSizeMb: number;
}>('[documentArchive] UPLOAD_FILES_TOO_LARGE');

export const uploadFileNamesDuplicated = actionCreator<{ fileNames: string }>(
  '[documentArchive] UPLOAD_FILE_NAMES_DUPLICATED'
);

export const attachmentRenameRejected = actionCreator(
  '[documentArchive] ATTACHMENT_RENAME_REJECTED'
);

export const uploadWorkspaceFiles = actionCreator<
  Trackable<UploadFilesPayload>
>('[documentArchive] UPLOAD_WORKSPACE_FILES');

export type DocumentArchiveFilters = [
  Extend<TableFilterString, { id: 'filename' }>,
  Extend<TableFilterList, { id: 'attachmentType' }>,
];

export type FilterExpression =
  TableFilterExpressionFrom<DocumentArchiveFilters>;

export const setFilterExpressions = actionCreator<FilterExpression[]>(
  '[documentArchive] SET_FILTER_EXPRESSIONS'
);

export const showDragOverlay = actionCreator<boolean>(
  '[documentArchive] SHOW_DRAG_OVERLAY'
);

export const setSortDescriptor = actionCreator<SortDescriptor | null>(
  '[documentArchive] SET_SORT_DESCRIPTOR'
);

export const setSearchTerm = actionCreator<string>(
  '[documentArchive] SET_SEARCH_TERM'
);

export const setScrollPosition = actionCreator<ScrollPosition>(
  '[documentArchive] SET_SCROLL_POSITION'
);

export const highlightRows = actionCreator<string[]>(
  '[documentArchive] HIGHLIGHT_ROW'
);

export const clearHighlightedRows = actionCreator<string[]>(
  '[documentArchive] CLEAR_HIGHLIGHTED_ROW'
);

export type OpenMoveToModalPayload = {
  folderIds: string[];
  attachmentIds: string[];
};

export const openMoveToModal = actionCreator<OpenMoveToModalPayload>(
  '[documentArchive] OPEN_MOVE_TO_MODAL'
);

export const closeMoveToModal = actionCreator(
  '[documentArchive] CLOSE_MOVE_TO_MODAL'
);

export const setMoveToModalDestination = actionCreator<string | 'root'>(
  '[documentArchive] SET_MOVE_TO_MODAL_DESTINATION'
);

export const setMoveToModalSearchTerm = actionCreator<string>(
  '[documentArchive] SET_MOVE_TO_MODAL_SEARCH_TERM'
);

export const setMoveToModalNewFolderName = actionCreator<string | null>(
  '[documentArchive] SET_MOVE_TO_MODAL_NEW_FOLDER_NAME'
);

export type ConfirmMoveToNewFolderPayload = {
  newFolderName: string;
  typeId: string;
  foldersToMove: APIDocumentArchiveFolder[];
  attachmentsToMove: APIAttachment[];
};

export const confirmMoveToNewFolder = actionCreator<
  Trackable<ConfirmMoveToNewFolderPayload>
>('[documentArchive] CONFIRM_MOVE_TO_NEW_FOLDER');

export const confirmMoveToNewWorkspaceFolder = actionCreator<
  Trackable<ConfirmMoveToNewFolderPayload>
>('[documentArchive] CONFIRM_MOVE_TO_NEW_WORKSPACE_FOLDER');
